import { NetworkStatus, useQuery, useReactiveVar } from '@apollo/client';
import {
  IColumn,
  makeStyles,
  SelectionMode,
  Stack,
  Text,
  TextView,
} from '@fluentui/react';
import '@xyflow/react/dist/style.css';
import { HighLightActiveLink } from 'common/components/HighLight';
import { InfiniteList } from 'common/components/InfiniteList';
import { ColumnData } from 'common/components/SearchBar';
import React, { useState } from 'react';
import { FlowApproval_Query } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/FlowApproval';
import { setGlobalSearchText } from 'utility/cache/ui';
import {
  ApprovalTestResults,
  ApprovalTestResults_approvalTestResults_nodes,
  ApprovalTestResultsVariables,
} from './__generated__/ApprovalTestResults';
import { getColumns } from './columns.data';
import { ApprovalTestView } from './Test';
import { dayMonthDateFormat } from 'common/utils/dateFormats';
import APPROVAL_TEST_RESULTS from './ApprovalTestResults.graphql';

export type ApprovalTestRowItem =
  ApprovalTestResults_approvalTestResults_nodes & {};

interface TestingProps {
  approvalDetailsData: FlowApproval_Query | undefined;
}

export const Testing: React.FC<TestingProps> = (props) => {
  const styles = useStyles();
  const [selectedList, setSelectedList] = useState<
    ApprovalTestResults_approvalTestResults_nodes[]
  >([]);

  const { columnArray } = getColumns();
  const [gridColumns] = useState<ColumnData[]>(columnArray);
  const globalSearchText = useReactiveVar(setGlobalSearchText);
  const [testCaseSelected, setTestCaseSelected] = useState<string | undefined>(
    undefined
  );

  const { loading, data, networkStatus, refetch } = useQuery<
    ApprovalTestResults,
    ApprovalTestResultsVariables
  >(APPROVAL_TEST_RESULTS, {
    skip: !props.approvalDetailsData?.approval?.id,
    variables: {
      filter: {
        approvalId: { equalTo: props.approvalDetailsData?.approval?.id! },
      },
      orderBy: '_FULL_COMMENT_ASC',
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const refetching = loading && networkStatus !== NetworkStatus.fetchMore;
  const transformedData = refetching
    ? undefined
    : data?.approvalTestResults?.nodes?.map((item) => {
        return {
          ...item,
        } as ApprovalTestRowItem;
      });

  const _renderItemColumn = (
    item: ApprovalTestRowItem | undefined,
    _index: number | undefined,
    column: IColumn | undefined
  ) => {
    if (item) {
      const fieldContent = item[
        column?.fieldName as keyof ApprovalTestRowItem
      ] as string;

      switch (column?.key) {
        case '_fullComment':
          return (
            <HighLightActiveLink
              highlightText={globalSearchText}
              text={item?._fullComment!}
              onClick={() => {
                setTestCaseSelected(item.id);
              }}
            />
          );

        case 'statusType':
          return (
            <Stack>
              <Text>{item.statusType?.statusType}</Text>
            </Stack>
          );
        case 'testDate':
          return (
            <Stack>
              <Text>
                {item.testDate ? dayMonthDateFormat(item.testDate) : '__'}
              </Text>
            </Stack>
          );
        case 'isGlobalTest':
          return (
            <Stack>
              <Text>{item.isGlobalTest ? 'Yes' : 'No'}</Text>
            </Stack>
          );
        default:
          return (
            <Stack
              verticalAlign="center"
              className={styles.statusColumnContainer}
            >
              {fieldContent && <TextView>{fieldContent}</TextView>}
            </Stack>
          );
      }
    }
  };

  const listColumns = gridColumns.filter((column) => column.isVisible);

  return (
    <Stack className={styles.bodyContainer}>
      <Stack
        horizontal
        horizontalAlign="space-between"
        tokens={{ padding: '10px 20px' }}
      >
        <Text variant="xLargePlus">Test cases</Text>
        <ApprovalTestView
          onSubmitComplete={(id) => {
            if (id) setTestCaseSelected(id);
          }}
          testCaseSelected={testCaseSelected}
          setTestCaseSelected={setTestCaseSelected}
          onRefresh={() => {
            refetch();
          }}
          onClose={() => {
            setTestCaseSelected(undefined);
          }}
          approvalDetailsData={props.approvalDetailsData}
          selectedList={selectedList}
        />
      </Stack>

      <Stack grow tokens={{ padding: '0px 25px', childrenGap: 20 }}>
        <InfiniteList
          items={transformedData || []}
          loading={loading}
          hasNextPage={false}
          showFooter={false}
          selectionMode={SelectionMode.multiple}
          columns={listColumns}
          onRenderItemColumn={_renderItemColumn}
          onSelectionChanged={setSelectedList}
        />
      </Stack>
    </Stack>
  );
};

export const useStyles = makeStyles((theme) => ({
  contentColumnAlignRight: {
    display: 'block',
    textAlign: 'right',
  },
  statusColumnContainer: {
    height: 25,
  },

  bodyContainer: {
    flex: 1,
    paddingTop: 50,
  },
}));
